import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@cosmos/angular/utils';
import { SimulationElement } from '@cosmos/shared';
import { catchError, map, Observable, Observer } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

export interface SimulationExportFile {
  blob: Blob;
  filename: string;
}

export type SimulationStreamEvent =
  | { type: 'progress'; pct: number }
  | { type: 'result'; data: SimulationElement }
  | { type: 'error'; message: string };

@Injectable({
  providedIn: 'root',
})
export class SimulatorApiService {
  private readonly base = `${environment.API_URL}/simulator`;

  constructor(private http: HttpClient, private errorService: ErrorService) {}

  // Internal computational primitive — used by the construction-tab scheduler only.
  // Never persisted, metered, or exportable. Do not call this from user-facing run flows.
  previewElement(projectId: string, dto: any, nodes?: number): Observable<SimulationElement> {
    const params = new URLSearchParams({ projectId });
    if (nodes) params.set('nodes', String(nodes));
    return this.http
      .post<SimulationElement>(`${this.base}/elements/preview?${params.toString()}`, dto)
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  // SSE streaming variant — emits progress, result or error events.
  // This is the user-facing single-element run path: persists on success, meters, tracks PostHog.
  runElementStream(projectId: string, dto: any, nodes?: number): Observable<SimulationStreamEvent> {
    const params = new URLSearchParams({ projectId });
    if (nodes) params.set('nodes', String(nodes));
    const url = `${this.base}/elements/run/stream?${params.toString()}`;

    return new Observable((observer: Observer<SimulationStreamEvent>) => {
      const controller = new AbortController();

      const getCookieValue = (name: string): string | null => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
      };

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      };
      const xsrfToken = getCookieValue('XSRF-TOKEN');
      if (xsrfToken) headers['X-XSRF-TOKEN'] = xsrfToken;

      fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(dto),
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok || !response.body) {
            const text = await response.text().catch(() => '');
            let message = text;
            try {
              const parsed = JSON.parse(text);
              if (parsed?.message) message = parsed.message;
            } catch { /* text was not JSON */ }
            observer.next({ type: 'error', message: message || 'Unknown error' });
            observer.complete();
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) { observer.complete(); break; }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              try {
                const event = JSON.parse(line.slice(6)) as SimulationStreamEvent;
                observer.next(event);
                if (event.type === 'result' || event.type === 'error') {
                  observer.complete();
                  return;
                }
              } catch {
                // malformed line — ignore
              }
            }
          }
        })
        .catch((err) => {
          if (err?.name === 'AbortError') { observer.complete(); return; }
          observer.next({ type: 'error', message: err?.message ?? 'Stream failed' });
          observer.complete();
        });

      // Teardown: abort fetch when subscriber unsubscribes (e.g. navigation away)
      return () => controller.abort();
    });
  }

  listElements(projectId: string, skip?: number, limit?: number): Observable<Partial<SimulationElement>[]> {
    const params = new URLSearchParams({ projectId });
    if (skip !== undefined) params.set('skip', String(skip));
    if (limit !== undefined) params.set('limit', String(limit));
    return this.http
      .get<Partial<SimulationElement>[]>(`${this.base}/elements?${params.toString()}`)
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  countElements(projectId: string): Observable<{ count: number }> {
    return this.http
      .get<{ count: number }>(`${this.base}/elements/count?projectId=${projectId}`)
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  countFavourites(projectId: string): Observable<{ count: number }> {
    return this.http
      .get<{ count: number }>(`${this.base}/elements/favourites-count?projectId=${projectId}`)
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  getElementById(id: string): Observable<SimulationElement> {
    return this.http
      .get<SimulationElement>(`${this.base}/elements/${id}`)
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  /**
   * Matches MeasurementsService.findById's shape (Observable<HttpResponse<T>>, .body access) —
   * required by BreadcrumbsResolverService.data, which calls `.findById(id, query)` generically
   * across every resolvable entity type. getElementById (above) stays the normal call site for
   * everything else — it returns the unwrapped body directly and many components already depend
   * on that shape.
   */
  findById(id: string, query = ''): Observable<HttpResponse<SimulationElement>> {
    return this.http
      .get<SimulationElement>(`${this.base}/elements/${id}${query}`, { observe: 'response' })
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  /**
   * Fetches the GCS-resident spatial field data (tFieldC/cornerTempC) for a simulation.
   * Not included in getElementById/listElements — fetch only when actually rendering a
   * heatmap (2D/3D viewer, or 1D's conditional heatmap card).
   */
  getFieldData(id: string): Observable<{ tFieldC: unknown; cornerTempC?: number[] }> {
    return this.http
      .get<{ tFieldC: unknown; cornerTempC?: number[] }>(`${this.base}/elements/${id}/field-data`)
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  deleteElement(id: string): Observable<void> {
    return this.http
      .delete<void>(`${this.base}/elements/${id}`)
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  favouriteElement(id: string, projectId: string): Observable<SimulationElement> {
    return this.http
      .patch<SimulationElement>(`${this.base}/elements/${id}/favourite?projectId=${projectId}`, {})
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  unfavouriteElement(id: string): Observable<SimulationElement> {
    return this.http
      .patch<SimulationElement>(`${this.base}/elements/${id}/unfavourite`, {})
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  /** Set (or clear, with an empty string) the optional user label. Returns the updated element. */
  renameElement(id: string, name: string): Observable<SimulationElement> {
    return this.http
      .patch<SimulationElement>(`${this.base}/elements/${id}/rename`, { name })
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  exportXlsx(id: string, projectId: string, locale: string, includeFullDataGrids: boolean, forceRegenerate = false): Observable<SimulationExportFile> {
    const params = new URLSearchParams({ projectId, locale, includeFullDataGrids: String(includeFullDataGrids), forceRegenerate: String(forceRegenerate) });
    return this.http
      .get(`${this.base}/elements/${id}/export/xlsx?${params.toString()}`, { responseType: 'blob', observe: 'response' })
      .pipe(
        map(res => ({
          blob: res.body as Blob,
          filename: this.extractFilename(res.headers.get('Content-Disposition')) ?? 'simulation-export.xlsx',
        })),
        catchError(err => { this.errorService.handleError(err); throw err; }),
      );
  }

  exportPdf(id: string, projectId: string, forceRegenerate = false, locale = 'es_ES'): Observable<SimulationExportFile> {
    const params = new URLSearchParams({ projectId, forceRegenerate: String(forceRegenerate), locale });
    return this.http
      .get(`${this.base}/elements/${id}/export/pdf?${params.toString()}`, { responseType: 'blob', observe: 'response' })
      .pipe(
        map(res => ({
          blob: res.body as Blob,
          filename: this.extractFilename(res.headers.get('Content-Disposition')) ?? 'simulation-report.pdf',
        })),
        catchError(err => { this.errorService.handleError(err); throw err; }),
      );
  }

  emailXlsx(id: string, projectId: string, locale: string, includeFullDataGrids: boolean): Observable<{ message: string }> {
    const params = new URLSearchParams({ projectId, locale, includeFullDataGrids: String(includeFullDataGrids) });
    return this.http
      .get<{ message: string }>(`${this.base}/elements/${id}/export/xlsx/email?${params.toString()}`)
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  emailPdf(id: string, projectId: string, locale = 'es_ES'): Observable<{ message: string }> {
    const params = new URLSearchParams({ projectId, locale });
    return this.http
      .get<{ message: string }>(`${this.base}/elements/${id}/export/pdf/email?${params.toString()}`)
      .pipe(catchError(err => { this.errorService.handleError(err); throw err; }));
  }

  private extractFilename(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;
    const match = contentDisposition.match(/filename=([^;]+)/);
    return match ? match[1].trim() : null;
  }
}
